/**
 * PAYMENTS MADE TO CONTRACTORS.
 *
 * Reported 17-09-2026, from the screen: Materials > Payments Made was listing
 * four payments and two of them were contractor payments against RA-1 of
 * WO-TR7. Its tiles are computed from that list, so "Total paid 45,213.00" was
 * a materials figure with 34,800 of works-contract money inside it.
 *
 * The same fault 0129 fixed for the payable and did not follow through on for
 * the payment. A contractor payment settles a CPAY payable, is recorded against
 * a running account bill, and its proof is the bank advice filed on that bill.
 * None of that is visible on a screen built for invoices and goods receipts.
 *
 * ONE LEDGER, TWO DOORS — the rule this module followed from 0129 to 0143.
 * These were the same vendor_payments rows the material purchases use, read
 * through the same v_payment_register, with Materials filtering ra_bill_id is
 * null and this screen filtering it not null.
 *
 * THAT IS NO LONGER SO. Board instruction, 19-09-2026: the two modules must
 * work completely independently, sharing only the Vendor master, Sites, UOM
 * and the Terms & Conditions. Filtering the rows off each other's screens
 * treated the symptom; the rows were still one table and a change to a
 * Materials view could still reshape this one. 0144 moved them into
 * contractor_payments, which nothing in Material Procurement reads or writes,
 * and this screen now reads v_contractor_payments over that table.
 *
 * WHY THERE IS NO "NEW PAYMENT" BUTTON HERE
 * -----------------------------------------
 * Deliberate, and the one place this screen differs from its Materials twin. A
 * contractor payment cannot be recorded without a bill: rpc_ra_record_payment
 * needs the RA bill for the ceiling, the payable and the site. A button here
 * would have to ask which bill first, which is a worse version of opening the
 * bill — where the net payable, what has already gone out and the proof upload
 * are all on screen together. So this is a register, and it links through.
 *
 * WHAT IT IS NUMBERED
 * -------------------
 * CPMT-, since 0141. Not CPAY-, which was already the contractor PAYABLE and
 * would have meant one number for two documents. CPMT-20260916-001 pays
 * CPAY-20260916-001, and the relationship is visible in the numbers.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Banknote, FilterX, Receipt, ShieldCheck, Undo2, Wallet,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Loading, Modal, SearchInput, Select, Stat,
  Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Mirrors rpc_cpmt_reverse. The RPC is the enforcement; this is the door. */
const MAY_REVERSE = ['admin', 'accounts_spoc']

interface CpmtRow {
  id: string
  payment_date: string
  vendor_id: string; vendor_name: string | null
  amount: number
  mode: string | null
  reference_no: string | null
  remarks: string | null
  recorded_by_name: string | null
  proof_status: string
  attachment_count: number
  ra_bill_id: string; ra_no: number
  wo_id: string; wo_title: string | null
  site_id: string; site_names: string | null
  net_payable: number
  payable_id: string | null
  reverses_id: string | null
  reversal_reason: string | null
}

const BLANK = { term: '', site: '', vendor: '', mode: '' }

export default function ContractorPayments() {
  const { profile } = useAuth()
  const toast = useToast()
  const [reversing, setReversing] = useState<CpmtRow | null>(null)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const { data: allSites } = useSites()
  const [f, setF] = useState({ ...BLANK })
  const { sort, toggle } = useSort('payment_date', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.values(f).some(Boolean)

  const { data: rows, loading, refresh } = useQuery<CpmtRow>(
    'v_contractor_payments',
    () => supabase.from('v_contractor_payments').select('*')
      .order('payment_date', { ascending: false }).limit(1000),
    [], { cacheable: false })

  const vendors = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) m.set(r.vendor_id, r.vendor_name ?? r.vendor_id)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [rows])

  const modes = useMemo(
    () => [...new Set(rows.map((r) => r.mode).filter(Boolean))].sort() as string[], [rows])

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return rows.filter((r) =>
      (!f.site || r.site_id === f.site)
      && (!f.vendor || r.vendor_id === f.vendor)
      && (!f.mode || r.mode === f.mode)
      // The payment number is IN the search, which is the whole reason 0129 was
      // written: on the Materials screen it was not, the tiles were computed
      // from the filtered list, and a man searching for a number he was holding
      // got "no payables yet" and thought he had lost his access.
      && (!t
        || r.id.toLowerCase().includes(t)
        || (r.vendor_name ?? '').toLowerCase().includes(t)
        || r.ra_bill_id.toLowerCase().includes(t)
        || r.wo_id.toLowerCase().includes(t)
        || (r.reference_no ?? '').toLowerCase().includes(t)))
  }, [rows, f])

  const sorted = useMemo(() => sortRows(shown, sort), [shown, sort])

  const total = shown.reduce((a, r) => a + n(r.amount), 0)
  const contractors = new Set(shown.map((r) => r.vendor_id)).size
  const noProof = shown.filter((r) => r.attachment_count === 0)

  const canReverse = MAY_REVERSE.includes(profile?.role ?? '')
  /** A row that another row cancels. Shown, struck through, never hidden. */
  const reversedIds = new Set(rows.map((r) => r.reverses_id).filter(Boolean) as string[])

  const reverse = async () => {
    if (!reversing) return
    setBusy(true)
    const { error } = await supabase.rpc('rpc_cpmt_reverse',
      { p_payment_id: reversing.id, p_reason: why.trim() })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not reverse it.')); return }
    setReversing(null); setWhy(''); refresh()
    toast.success('Reversed. The payable is open again for that amount.')
  }

  if (loading) return <Loading label="Reading the payments…" />

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Payments to Contractors</h1>
        <p className="text-sm text-slate-500">
          Money paid against certified running account bills. Numbered <strong>CPMT-</strong>,
          and each one settles the <strong>CPAY-</strong> payable its bill raised.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Payments" value={shown.length}
              sub={dirty ? 'matching your filters' : 'all time'}
              icon={<Banknote className="h-4 w-4" />} />
        <Stat label="Total paid" value={rupees(total)}
              icon={<Wallet className="h-4 w-4" />} />
        <Stat label="Contractors paid" value={contractors}
              icon={<Receipt className="h-4 w-4" />} />
        <Stat label="Without proof on file" value={noProof.length}
              tone={noProof.length ? 'bad' : 'good'}
              sub={noProof.length ? rupees(noProof.reduce((a, r) => a + n(r.amount), 0)) : 'every one filed'}
              icon={noProof.length
                ? <AlertTriangle className="h-4 w-4" />
                : <ShieldCheck className="h-4 w-4" />} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Receipt className="mr-1 inline h-3.5 w-3.5" />
        A payment is recorded <strong>on the bill</strong>, where the net payable, what has
        already gone out and the bank advice are all on screen together — open the RA bill
        from any row below. Since 0144 these are the works module&rsquo;s own rows in
        <strong> contractor_payments</strong>; Material Procurement neither reads nor writes
        them, and its own Payments Made screen shows only invoices and advances.
      </div>

      <Card
        title="All contractor payments"
        subtitle="Click the payment to open the payable it settles, or the bill or work order beside it."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="CPMT, contractor, RA bill, WO, UTR…" />
            <Select className="w-auto" value={f.site} onChange={(e) => set({ site: e.target.value })}
                    placeholder="All sites"
                    options={allSites.map((s: any) => ({ value: s.id, label: s.site_name }))} />
            <Select className="w-auto" value={f.vendor} onChange={(e) => set({ vendor: e.target.value })}
                    placeholder="All contractors"
                    options={vendors.map(([id, name]) => ({ value: id, label: name }))} />
            <Select className="w-auto" value={f.mode} onChange={(e) => set({ mode: e.target.value })}
                    placeholder="All modes"
                    options={modes.map((m) => ({ value: m, label: m }))} />
            {dirty && (
              <button className="btn-ghost btn-sm" onClick={() => setF({ ...BLANK })}>
                <FilterX className="h-4 w-4" /> Clear
              </button>
            )}
          </div>
        }
      >
        {sorted.length === 0 ? (
          <EmptyState icon={<Banknote className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'No contractor payments yet'}
                      message={dirty
                        ? 'Clear the filters to see everything.'
                        : 'A payment appears here once Accounts records one against an approved running account bill.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Payment</Th>
                  <Th id="payment_date" sort={sort} onSort={toggle}>Paid on</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <th>Against</th>
                  <Th id="mode" sort={sort} onSort={toggle}>Mode</Th>
                  <th>UTR / reference</th>
                  <Th id="amount" sort={sort} onSort={toggle} align="right">Amount</Th>
                  <th>Proof</th>
                  <th>Recorded by</th>
                  {canReverse && <th />}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const isContra = !!r.reverses_id
                  const wasReversed = reversedIds.has(r.id)
                  return (
                  <tr key={r.id} className={isContra ? 'bg-slate-50/70' : ''}>
                    {/*
                      THE PAYMENT NUMBER OPENS SOMETHING.
                      Reported 20-09-2026: it was plain text, so the one
                      identifier in the row that names THIS record was the one
                      thing that did not click.
                      It opens the PAYABLE it settles, not the bill: the bill
                      is already linked in the next column but one, and the
                      payable is the document this payment discharges -- its
                      detail page carries the money ladder with this payment
                      inside it. A payment with no payable is a data fault, and
                      falls back to the bill rather than to nothing.
                    */}
                    <td className="whitespace-nowrap font-medium">
                      <Link to={r.payable_id
                                 ? `/service/contractor-payables/${r.payable_id}`
                                 : `/service/ra-bills/${r.ra_bill_id}`}
                            className="text-amber-700 hover:underline">
                        {r.id}
                      </Link>
                      {isContra ? (
                        <span className="block text-[11px] text-amber-700">
                          reverses {r.reverses_id}
                        </span>
                      ) : wasReversed ? (
                        <span className="block text-[11px] text-red-700">reversed</span>
                      ) : r.payable_id ? (
                        <span className="block text-[11px] text-slate-400">
                          settles {r.payable_id}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.payment_date)}</td>
                    <td className="max-w-[180px] truncate">
                      {r.vendor_name}
                      <span className="block max-w-[180px] truncate text-[11px] text-slate-400">
                        {r.site_names}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      <Link to={`/service/ra-bills/${r.ra_bill_id}`}
                            className="font-medium text-amber-700 hover:underline">
                        RA-{r.ra_no}
                      </Link>
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="block text-[11px] text-slate-400 hover:text-amber-700 hover:underline">
                        {r.wo_id}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap text-xs">{r.mode}</td>
                    <td className="max-w-[140px] truncate text-xs text-slate-500">
                      {r.reference_no || '—'}
                    </td>
                    <td className={`num whitespace-nowrap font-semibold ${
                      n(r.amount) < 0 ? 'text-red-700' : wasReversed ? 'text-slate-400 line-through' : ''}`}>
                      {rupees(r.amount)}
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      {r.attachment_count > 0
                        ? <span className="pill-green">Filed</span>
                        : <span className="pill-amber" title="No bank advice on the bill">None</span>}
                    </td>
                    <td className="max-w-[140px] truncate text-xs text-slate-500">
                      {r.recorded_by_name ?? '—'}
                    </td>
                    {canReverse && (
                      <td className="whitespace-nowrap text-right">
                        {/*
                          Reversal, never deletion — 0165. The contra is a second
                          row for the negative amount, so the payable reopens and
                          every total nets correctly. A payment already reversed,
                          and a reversal itself, offer nothing.
                        */}
                        {!isContra && !wasReversed && (
                          <button className="btn-ghost btn-sm"
                                  onClick={() => { setWhy(''); setReversing(r) }}>
                            <Undo2 className="h-4 w-4" /> Reverse
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={!!reversing} onClose={() => setReversing(null)}
             title={reversing ? `Reverse ${reversing.id}` : ''}
             subtitle={reversing
               ? `${rupees(reversing.amount)} to ${reversing.vendor_name ?? reversing.vendor_id}`
               : ''}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setReversing(null)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-warning" disabled={busy || why.trim().length < 10}
                         onClick={() => void reverse()}>
                   {busy ? 'Reversing…' : 'Reverse it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted">
            A second payment is written for the negative of this one, pointing back at it.
            Both stay on the register for ever. The payable reopens for that amount and the
            bill can be paid again.
          </Alert>
          <Field label="Why" required
                 hint="At least a short sentence. It is kept beside the payment it cancels.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="Entered against the wrong bill; the money went to RA-2 of the same order." />
          </Field>
          {why.trim().length > 0 && why.trim().length < 10 && (
            <p className="text-xs text-amber-700">A few more words — {10 - why.trim().length} to go.</p>
          )}
        </div>
      </Modal>
    </div>
  )
}
